<template>
  <v2-modal
    :show="show"
    title="Connection Request"
    size="lg"
    @close="$emit('close')"
  >
    <div
      v-if="!loaded"
      class="v2-flex v2-justify-center v2-p-8"
    >
      <v2-spinner size="md" />
    </div>
    <div
      v-else
      class="v2-connection-request"
    >
      <p class="v2-text-sm v2-text-muted v2-mb-4">
        <span v-if="isRetailer">Request access to shop from this brand:</span>
        <span v-else>You have received a new connection request:</span>
      </p>

      <div class="v2-connection-request__profiles">
        <!-- Retailer info -->
        <div class="v2-connection-request__profile">
          <p class="v2-text-sm v2-font-medium v2-mb-2">
            {{ isRetailer ? 'Your info:' : 'Retailer info:' }}
          </p>
          <div class="v2-flex v2-items-center v2-gap-4">
            <img
              :src="retailerData.logo || defaultImage"
              :alt="retailerData.entityName"
              class="v2-connection-request__avatar"
            >
            <div>
              <p class="v2-font-semibold">
                {{ retailerData.entityName }}
              </p>
              <p class="v2-text-sm v2-text-muted">
                {{ retailerData.country }}
              </p>
              <div class="v2-flex v2-gap-2 v2-mt-1">
                <a
                  v-for="link in getEntityLinks(retailerData)"
                  :key="link.type"
                  :href="link.url"
                  target="_blank"
                  class="v2-connection-request__social-link"
                >
                  <component :is="link.type + '-icon-svg'" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="v2-connection-request__arrow">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12"
            />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </div>

        <!-- Brand info -->
        <div class="v2-connection-request__profile">
          <p class="v2-text-sm v2-font-medium v2-mb-2">
            {{ isRetailer ? 'Requesting access to:' : 'Your info:' }}
          </p>
          <div class="v2-flex v2-items-center v2-gap-4">
            <img
              :src="brandData.logo || defaultImage"
              :alt="brandData.entityName"
              class="v2-connection-request__avatar"
            >
            <div>
              <p class="v2-font-semibold">
                {{ brandData.entityName }}
              </p>
              <p class="v2-text-sm v2-text-muted">
                {{ brandData.country }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="v2-mt-4">
        <p class="v2-text-sm v2-font-medium v2-mb-1">
          {{ isRetailer ? 'Message to Brand (Optional):' : 'Message From Retailer:' }}
        </p>
        <textarea
          v-model="message"
          class="v2-connection-request__textarea"
          rows="4"
          maxlength="140"
          :disabled="isBrand"
          :placeholder="isRetailer ? 'Introduce your store and let the brand know why they\'re the perfect fit for you...' : 'No Message'"
        />
      </div>

      <!-- Retailer actions -->
      <div
        v-if="isUpdateEnabledRetailerLocal"
        class="v2-flex v2-justify-center v2-gap-3 v2-mt-4"
      >
        <v2-button
          variant="outline"
          @click="$emit('close')"
        >
          Cancel
        </v2-button>
        <v2-button @click="onSubmitRequest">
          {{ isUpdateEnabled ? 'Save changes' : 'Submit request' }}
        </v2-button>
      </div>

      <!-- Brand actions -->
      <div
        v-if="isBrand"
        class="v2-flex v2-justify-center v2-gap-3 v2-mt-4"
      >
        <v2-button
          variant="outline"
          @click="onUpdateRequest(CONNECTION_REQUEST_STATUS.DECLINED)"
        >
          Reject
        </v2-button>
        <v2-button @click="onUpdateRequest(CONNECTION_REQUEST_STATUS.APPROVED)">
          Accept Request
        </v2-button>
      </div>
    </div>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Button from '@/components/v2/V2Button.vue'
import { getLinkWithhttp } from '@/common-utils'
import { useConnectionRequest } from '@/composables/useConnectionRequest'

export default {
  name: 'V2ConnectionRequestModal',
  components: {
    V2Modal,
    V2Spinner,
    V2Button,
  },
  props: {
    show: { type: Boolean, default: false },
    brandId: { type: String, default: null },
    retailerId: { type: String, default: null },
    requestId: { type: String, default: null },
    status: { type: String, default: null },
    requestMessage: { type: String, default: null },
  },
  setup() {
    return { ...useConnectionRequest() }
  },
  data() {
    return {
      defaultImage: require('@/assets/images/elements/default-product.jpeg'),
    }
  },
  computed: {
    isUpdateEnabled() {
      return this.isRetailer && this.$root.$route.name === 'wallet'
    },
    isUpdateEnabledRetailerLocal() {
      return this.isUpdateEnabledRetailer(this.status)
    },
    isButtonEnabledLocal() {
      return this.isButtonEnabled(this.status)
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.fetchConnectionInfo({ brandId: this.brandId, retailerId: this.retailerId })
      }
    },
  },
  methods: {
    getEntityLinks(entity) {
      const links = []
      const linkTypes = ['website', 'instagram', 'facebook', 'twitter', 'linkedin']
      linkTypes.forEach(type => {
        const url = getLinkWithhttp(entity[`${type}Link`])
        if (url) {
          links.push({ type, url })
        }
      })
      return links
    },
    async onSubmitRequest() {
      if (this.isButtonEnabledLocal) {
        this.onUpdateRequest(this.CONNECTION_REQUEST_STATUS.PENDING)
      } else {
        const { success } = await this.submitNewRequest(this.brandId)
        if (success) {
          this.$emit('close')
          this.$emit('update-request')
        }
      }
    },
    async onUpdateRequest(status) {
      const { success } = await this.updateExistingRequest(
        this.requestId,
        status,
        this.requestMessage,
      )
      if (success) {
        this.$emit('update-request')
        this.$emit('close')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-connection-request {
  &__profiles {
    display: flex;
    align-items: center;
    gap: var(--v2-space-4);
    flex-wrap: wrap;
  }

  &__profile {
    flex: 1;
    min-width: 200px;
    padding: var(--v2-space-4);
    background: var(--v2-muted);
    border-radius: var(--v2-radius-md);
  }

  &__avatar {
    width: 64px;
    height: 64px;
    border-radius: var(--v2-radius-md);
    object-fit: cover;
    border: 1px solid var(--v2-border);
  }

  &__arrow {
    color: var(--v2-muted-foreground);
    flex-shrink: 0;
  }

  &__social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: var(--v2-muted-foreground);
    transition: color 0.15s ease;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__textarea {
    width: 100%;
    padding: var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-background);
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-md);
    resize: vertical;
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: var(--v2-muted);
    }
  }
}
</style>
